/**
 * Decide what to do when the tenant bootstrap query (`/auth/bootstrap` / `/auth/me`) errors. Pure +
 * framework-free so it is unit-testable in the node-env vitest setup (the provider that uses it is a `.tsx`
 * React component and cannot be rendered there).
 *
 * HARD RULE (regression for the production logout-loop incident): ONLY a 401 means the session itself is
 * invalid and may clear the authoritative auth context. A 403 (authenticated but not authorized) or any
 * 5xx / network / unknown error must NEVER tear down the session — doing so turned a transient backend blip
 * into a forced logout that bounced between `/auth/failure` and `/no-access` ~12 times in 6 seconds.
 *
 * SECOND HARD RULE: automatic retrying is BOUNDED, and some errors are not worth a single automatic retry.
 * A 429 says "you are asking too often", and answering it by asking again is how a slow session check turned
 * into a self-inflicted rate-limit: the bucket is 60 requests per minute on `/tenant/auth/*`, and a
 * free-running retry reaches that in seconds and then never recovers on its own.
 */
export type BootstrapErrorRedirect = "login" | "no-access" | "auth-failure";

export type BootstrapErrorAction = {
  /** Whether to clear the authoritative auth context + persisted branch access. Only true for a 401. */
  clearAuthContext: boolean;
  /** Where to send the user. Each is a terminal landing; the caller navigates only if not already there. */
  redirect: BootstrapErrorRedirect;
  /**
   * Whether asking again could plausibly produce a different answer.
   *
   * False for a settled verdict (401/403 — the server has decided, and repeating the question is noise) and
   * for 429, where repeating the question IS the problem. True only for the genuinely transient class:
   * 5xx, network failures, and unknown errors.
   */
  retryable: boolean;
};

export function resolveBootstrapErrorAction(status: number | null | undefined): BootstrapErrorAction {
  // Invalid/expired session: clear context and re-authenticate. The server has decided; asking again cannot
  // change it.
  if (status === 401) {
    return { clearAuthContext: true, redirect: "login", retryable: false };
  }
  // Authenticated but forbidden: stable no-access page, session preserved. Also a settled verdict.
  if (status === 403) {
    return { clearAuthContext: false, redirect: "no-access", retryable: false };
  }
  /*
   * Rate limited. The one error whose cause is the retrying itself, so it gets the failure page and NO
   * automatic attempt — the window has to be allowed to pass, and only the user pressing Retry may spend
   * another request.
   */
  if (status === 429) {
    return { clearAuthContext: false, redirect: "auth-failure", retryable: false };
  }
  // Transient / server / network / unknown: recoverable failure page, session preserved (never a logout).
  return { clearAuthContext: false, redirect: "auth-failure", retryable: true };
}

/**
 * Whether the app may spend one more automatic session check.
 *
 * Extracted as a pure function for the same reason the action above is: the bound is the whole point of this
 * fix, and a bound that lives inline in a `.tsx` effect cannot be tested in the node-env suite — so it would
 * be asserted by reading it, which is how the previous "single retry" turned out to be unbounded in practice.
 */
export function canRetryBootstrap(params: {
  status: number | null | undefined;
  /** Automatic attempts already spent in this error episode. */
  attemptsSpent: number;
  maxAttempts: number;
}): boolean {
  if (!resolveBootstrapErrorAction(params.status).retryable) return false;
  // The failing request that got us here already counts, so the last slot buys no further attempt.
  return params.attemptsSpent < params.maxAttempts - 1;
}
